<template>
  <v-container class="fill-height" fluid>
    <v-row align="center" justify="center">
      <!-- wide enough for each consent to stay on one line -->
      <v-col cols="12" sm="10" md="8" lg="6" xl="5">
        <v-card class="pa-6 signup-card">
          <v-card-title class="text-center text-h4 mb-4">
            Регистрация
          </v-card-title>

          <v-alert
            v-if="showAlert"
            type="error"
            variant="tonal"
            closable
            class="mb-4"
            @click:close="showAlert = false"
          >
            {{ errorMessage }}
          </v-alert>

          <!-- a signed-in user gets no form (02.11.2026): his own invitation is accepted on «Мои
               команды», a new account needs a sign-out first; the link (code, address) is kept -->
          <div v-if="!authReady" class="text-center py-6">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <template v-else-if="signedInAs">
            <v-alert type="info" variant="tonal" class="mb-4">
              <template v-if="inviteForMe">
                Это приглашение для вас: вы уже вошли как <strong>{{ signedInAs }}</strong>.
                Примите его на странице «Мои команды».
              </template>
              <template v-else>
                Вы вошли как <strong>{{ signedInAs }}</strong>. Чтобы зарегистрировать новый аккаунт,
                сначала выйдите.
              </template>
            </v-alert>
            <div class="d-flex justify-center flex-wrap ga-2">
              <v-btn v-if="inviteForMe" color="primary" variant="flat" to="/teams">Открыть «Мои команды»</v-btn>
              <template v-else>
                <v-btn variant="text" to="/dashboard">Остаться</v-btn>
                <v-btn color="primary" variant="flat" :loading="leaving" @click="signOutAndStay">
                  Выйти и продолжить регистрацию
                </v-btn>
              </template>
            </div>
          </template>
          <v-form v-else @submit.prevent="handleSubmit" ref="form">
            <v-text-field
              v-model="email"
              name="email"
              autocomplete="username"
              label="Почта"
              type="email"
              variant="outlined"
              required
              :rules="emailRules"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="username"
              name="name"
              autocomplete="nickname"
              label="Ваше имя или никнейм"
              type="text"
              variant="outlined"
              required
              :rules="usernameRules"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="password"
              name="new-password"
              autocomplete="new-password"
              label="Пароль"
              type="password"
              variant="outlined"
              required
              :rules="passwordRules"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="confirmPassword"
              name="confirm-password"
              autocomplete="new-password"
              label="Ещё раз пароль"
              type="password"
              variant="outlined"
              required
              :rules="confirmPasswordRules"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="inviteCode"
              name="invite-code"
              autocomplete="off"
              label="Код приглашения"
              type="text"
              variant="outlined"
              required
              :rules="inviteCodeRules"
              class="mb-4"
            ></v-text-field>

            <!-- Согласия: отдельное действие на каждое, заранее ничего не отмечено -->
            <v-checkbox v-model="acceptTerms" :rules="termsRules" color="primary" density="compact" hide-details="auto">
              <template #label>
                <span>
                  Принимаю
                  <router-link :to="legalLinks.terms" target="_blank" @click.stop>условия использования сервиса</router-link>.
                </span>
              </template>
            </v-checkbox>
            <v-checkbox v-model="consentPersonalData" :rules="personalDataRules" color="primary" density="compact" hide-details="auto">
              <template #label>
                <span>
                  Даю
                  <router-link :to="legalLinks.consentAccount" target="_blank" @click.stop>согласие на обработку персональных данных</router-link>
                  для регистрации и использования сервиса.
                </span>
              </template>
            </v-checkbox>
            <v-checkbox
              v-model="consentNews"
              label="Хочу получать информационные новости об изменениях сервиса по email."
              color="primary"
              density="compact"
              hide-details
            />
            <div class="text-caption text-medium-emphasis ml-10 mb-3">
              Необязательно. Условия рассылки — в
              <router-link :to="legalLinks.consentNews" target="_blank">согласии на рассылку</router-link>.
              Отписаться можно в любой момент без закрытия аккаунта.
            </div>
            <div class="text-caption text-medium-emphasis mb-4">
              Подробнее об обработке данных — в
              <router-link :to="legalLinks.privacy" target="_blank">Политике в отношении обработки персональных данных</router-link>.
            </div>

            <v-btn
              type="submit"
              color="primary"
              size="large"
              block
              class="mb-4"
            >
              Регистрация
            </v-btn>

            <v-row justify="center" class="mt-4">
              <v-col cols="auto">
                <router-link to="/signin" class="text-primary text-decoration-none">
                  Вход
                </router-link>
              </v-col>
              <v-col cols="auto">
                <router-link to="/restore-password" class="text-primary text-decoration-none">
                  Восстановление пароля
                </router-link>
              </v-col>
            </v-row>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import apiClient from "@/apiClient";
import { LEGAL_LINKS, LEGAL_VERSIONS } from "@/config/legal";

export default {
  data() {
    return {
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      inviteCode: '',
      leaving: false,
      acceptTerms: false,
      consentPersonalData: false,
      consentNews: false,
      legalLinks: LEGAL_LINKS,
      showAlert: false,
      errorMessage: '',
      emailRules: [
        v => !!v || 'Почта обязательна',
        v => /.+@.+\..+/.test(v) || 'Почта должна быть валидной',
      ],
      usernameRules: [
        v => !!v || 'Имя или никнейм обязательны',
        v => (v && v.length >= 2) || 'Имя должно содержать минимум 2 символа',
      ],
      passwordRules: [
        v => !!v || 'Пароль обязателен',
        v => (v && v.length >= 8) || 'Пароль должен содержать минимум 8 символов',
        v => (v && /[A-Z]/.test(v)) || 'Пароль должен содержать заглавную букву',
        v => (v && /[a-z]/.test(v)) || 'Пароль должен содержать строчную букву',
        v => (v && /\d/.test(v)) || 'Пароль должен содержать цифру'
      ],
      confirmPasswordRules: [
        v => !!v || 'Подтверждение пароля обязательно',
        v => v === this.password || 'Пароли не совпадают',
      ],
      inviteCodeRules: [
        v => !!v || 'Код приглашения обязателен',
      ],
      termsRules: [
        v => !!v || 'Без принятия условий регистрация невозможна',
      ],
      personalDataRules: [
        v => !!v || 'Без согласия на обработку данных регистрация невозможна',
      ],
    };
  },
  computed: {
    authReady() {
      return this.$store.getters.isAuthInitialized
    },
    signedInAs() {
      const info = this.$store.state.userInfo
      return this.$store.state.isLoggedIn && info ? info.email : ''
    },
    // the link of a letter is for the signed-in user himself
    inviteForMe() {
      const email = this.$route.query.email
      return typeof email === 'string' && email.trim().toLowerCase() === (this.signedInAs || '').toLowerCase()
    },
  },
  created() {
    // the link of a letter of a team owner: /signup?code=...&email=... (the code is for this address)
    const { code, email } = this.$route.query
    if (typeof code === 'string') this.inviteCode = code
    if (typeof email === 'string') this.email = email
  },
  methods: {
    async signOutAndStay() {
      // the same page stays open: the form shows with the code and the address of the link
      this.leaving = true
      try {
        await this.$store.dispatch('logout')
      } finally {
        this.leaving = false
      }
    },
    async handleSubmit() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;
      const userData = {
        email: this.email,
        username: this.username,
        password: this.password,
        confirm_password: this.confirmPassword,
        invite_code: this.inviteCode,
        accept_terms: this.acceptTerms,
        consent_personal_data: this.consentPersonalData,
        consent_news: this.consentNews,
        legal_versions: LEGAL_VERSIONS
      };
      apiClient.post('/register', userData)
          .then(response => {
            if (response.status === 200) {
              // Save email for resend functionality
              sessionStorage.setItem('registeredEmail', this.email);
              this.$router.push({
                path: '/user-registered',
                query: { email: this.email }
              });
            } else {
              this.showError('Ошибка регистрации, попробуйте ещё раз');
            }
          })
          .catch(error => {
            this.showError(error.response && error.response.data ? error.response.data.detail : 'Ошибка регистрации, попробуйте ещё раз');
          });
    },
    showError(errorMessage) {
      this.errorMessage = errorMessage;
      this.showAlert = true;
    }
  },
  mounted() {
    // Vuetify validation is handled automatically
  }
};
</script>

<style scoped>
.signup-card {
  max-width: 760px;
  margin: 0 auto;
}
/* a long consent wraps beside its box instead of running over it */
.signup-card :deep(.v-checkbox .v-selection-control) {
  align-items: flex-start;
}
.signup-card :deep(.v-checkbox .v-label) {
  white-space: normal;
  opacity: 1;
  /* Vuetify stretches a one-line label to 40 px and centres the text in it, 7 px below the box: the
     label keeps its own height and the middle of its first line meets the middle of the box */
  align-self: flex-start;
  align-items: flex-start;
  height: auto;
  padding-top: calc((var(--v-selection-control-size) - 1.4em) / 2);
  line-height: 1.4;
}
</style>
